import { ReactNode, useState } from 'react';
import { startCase } from 'lodash';
import styled from 'styled-components';

import SourceBadges from 'components/shared/ai/SourceBadges';
import { useCitationViewer, WORKFLOW_ATTACHABLE_MODEL } from 'components/shared/ai/useCitationViewer';
import Button from 'components/shared/Button';
import { mobileTouchTarget } from 'components/shared/responsiveness/touchTarget';
import StandardModal from 'components/shared/StandardModal';
import { simpleDate } from 'lib/date';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { FieldLabel, FieldValue, InlineLink, StepDescription } from './categoryCard/styles';
import { labelForValue } from './actionConfig';
import { VALUE_NAMES } from './constants';
import { coverageLabel, COVERAGES_VALUE, currentCoverages, ExtractedCoverage } from './coverages';
import DemographicConflictPopover from './DemographicConflictPopover';
import {
  ADDRESS_KEY,
  ADDRESS_VALUES,
  coverageConflictKey,
  DemographicConflict,
  demographicConflicts,
  isPacketReading,
  packetCoverageFor,
  PATIENT_NAME_KEY,
} from './demographicConflicts';
import { ExtractionSource, extractionSources, mergeSources } from './extractionSources';
import { RestartConsentStep } from './RestartConsentModal';
import RevealableSsn from './RevealableSsn';
import { maskSsn } from './ssn';
import useResolveDemographicConflict from './useResolveDemographicConflict';

// The patient's demographics as the referral is using them, shown inside the completed Patient
// Demographics step ("Extract Demographics" until OLIO-3252) — with every field the packet reads
// differently highlighted, so a typed value that outranked the packet can't hide a disagreement.
//
// The LAYOUT is declared here (UX, 2026-08-04): who the patient is on one row, what they're
// insured by on the next. The DATA still comes from the action's own `produces` — so this is a
// spec applied to the API's list, not a replacement for it. Anything produced that the spec
// doesn't mention renders in a trailing row rather than disappearing, which is what keeps a value
// added to `produces` later from silently vanishing off this card.

// One field as rendered: a label and one or more lines under it. `node` overrides the lines for
// the one value that isn't plain text — the SSN, which carries its own reveal control.
//
// `conflict` marks a field the packet disagrees with (OLIO-3252): it renders highlighted, and
// `current` / `packet` are the two readings its panel shows side by side.
interface Cell {
  key: string;
  label: string;
  lines: string[];
  node?: ReactNode;
  conflict?: DemographicConflict;
  current?: ReactNode;
  packet?: ReactNode;
  // Where the packet said this value, shown only while the value in use IS the packet's reading:
  // an edited or portal value was not read from these documents. A conflict's packet side carries
  // its own, in `packetSources`.
  sources?: ExtractionSource[];
  packetSources?: ExtractionSource[];
}

// Abbreviated on this card only. The API's value catalog is the label source everywhere else
// (OLIO-2574) and stays untouched — renaming `patient_dob` to "DOB" in the catalog would change
// it on every other surface too, which UX asked for here and nowhere else.
const CARD_LABELS: Record<string, string> = {
  [VALUE_NAMES.PATIENT_DOB]: 'DOB',
  [VALUE_NAMES.PATIENT_GENDER]: 'Gender',
  [VALUE_NAMES.PATIENT_SSN]: 'SSN',
  // Only older referrals still carry this — referral intake stopped capturing it in OLIO-2890.
  // Labelled "Plan Type" rather than the catalog's "Payer Name" because it sits directly under
  // the per-coverage "Payer Name (Primary)" holding a *different* string, and because what it
  // held was payer and plan together.
  [VALUE_NAMES.PLAN_TYPE]: 'Plan Type',
  [VALUE_NAMES.PHONE_NUMBER]: 'Phone',
  [VALUE_NAMES.EXPECTED_DISCHARGE_DATE]: 'Projected Discharge',
  [VALUE_NAMES.PLAN_TYPE_CLASSIFICATION_ID]: 'Plan Type',
};

// The patient's name is shown as one field, but it's stored as three values — so it's composed
// here and the name values are consumed by it rather than rendered on their own.
const NAME_VALUES: string[] = [
  VALUE_NAMES.PATIENT_FIRST_NAME,
  VALUE_NAMES.PATIENT_MIDDLE_NAME,
  VALUE_NAMES.PATIENT_LAST_NAME,
];

// Row 1 after the composed name, in this order. Then the address, insurance and source rows.
const PATIENT_ROW: string[] = [VALUE_NAMES.PATIENT_DOB, VALUE_NAMES.PATIENT_GENDER, VALUE_NAMES.PATIENT_SSN];
const INSURANCE_ROW: string[] = [COVERAGES_VALUE, VALUE_NAMES.PLAN_TYPE];
// Placed deliberately rather than left to the catch-all unplaced row below, now that resolving it
// makes it a reported dimension (OLIO-3158). Its own row rather than an appendix to the insurance
// one: where a referral came from is not an insurance detail, and it is what a coordinator scans
// for when triaging.
const SOURCE_ROW: string[] = [VALUE_NAMES.REFERRAL_SOURCE];
// What a referral bridged from an Olio referral carries beyond the demographics. No step produces these, so only
// a card handed them in `fields` (Patient Demographics) shows them.
const OLIO_REFERRAL_ROW: string[] = [
  VALUE_NAMES.PHONE_NUMBER,
  VALUE_NAMES.EXPECTED_DISCHARGE_DATE,
  VALUE_NAMES.PLAN_TYPE_CLASSIFICATION_ID,
];

// Both sides of a name conflict, every part as stored. patientNameFrom drops a middle name the
// first name repeats, which would show "Jose M" with middle "M" and "Jose" with middle "M" as the
// same name, so the coordinator couldn't see what Use this value changes.
function spelledOut(part: (name: string) => unknown): string {
  return NAME_VALUES.map(part)
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .join(' ');
}

export function addressLines(value: (name: string) => unknown): string[] {
  const part = (name: string) => {
    const v = value(name);
    return v == null || typeof v === 'object' ? '' : String(v).trim();
  };
  const join = (parts: string[], separator: string) => parts.filter(Boolean).join(separator);
  return [
    join([part(VALUE_NAMES.PATIENT_ADDRESS_LINE_1), part(VALUE_NAMES.PATIENT_ADDRESS_LINE_2)], ', '),
    join(
      [part(VALUE_NAMES.PATIENT_CITY), join([part(VALUE_NAMES.PATIENT_STATE), part(VALUE_NAMES.PATIENT_ZIP)], ' ')],
      ', '
    ),
  ].filter(Boolean);
}

function scalarLines(name: string, value: unknown): string[] {
  if (value == null || value === '') return [];
  // A produced value this card has no rendering for is SKIPPED, not stringified. `produces` comes
  // from the API's published contract, so api can start producing a new compound value at any
  // time — and does, ahead of the web deploy that teaches this card to render it. Without this
  // guard that window is a broken page: an array or object reaching JSX throws and the error
  // boundary swallows the whole referral (which is exactly what happened with `coverages` on
  // 2026-08-04). Skipping means the value is invisible until the web side ships, which is the
  // right failure: nothing is claimed that isn't understood.
  if (typeof value === 'object') return [];
  if (name === VALUE_NAMES.PATIENT_SSN) return [maskSsn(String(value))];
  if (name === VALUE_NAMES.PATIENT_DOB || name === VALUE_NAMES.EXPECTED_DISCHARGE_DATE) {
    return [simpleDate(String(value)) ?? String(value)];
  }
  if (name === VALUE_NAMES.PATIENT_GENDER) return [startCase(String(value))];
  return [String(value)];
}

// Each coverage becomes its own pair of columns — "Payer Name (Primary)", "Member ID (Primary)" —
// rather than one packed line, so the row scans across like the rest of the card. The suffix comes
// from the coverage's own sequence, so a third one needs no change here.
// Takes coverages already narrowed by currentCoverages — a raw cast would let a null or non-object
// entry reach coverageLabel, which reads `.sequence` off it and throws mid-render with nothing
// above the referral page to catch it.
// A cell takes the key of the conflict raised on its own coverage and field, matched by facet rather
// than by sequence word: a coverage labelled some other way ("Medicare") is matched to its rank by
// position, so its label and its conflict's key name different things.
function coverageCells(
  coverages: ExtractedCoverage[],
  conflicts: DemographicConflict[],
  sourcesFor: (coverage: ExtractedCoverage, field: string) => ExtractionSource[]
): Cell[] {
  return coverages.flatMap((coverage, index) => {
    const label = coverageLabel(coverage);
    const suffix = label ? ` (${label})` : '';
    const rank = coverage.sequence?.toLowerCase() ?? String(index);
    const keyFor = (name: string) =>
      conflicts.find(
        (conflict) =>
          coverage.facetId !== undefined &&
          conflict.facetId === coverage.facetId &&
          conflict.fields.some((field) => field.name === name)
      )?.key ?? coverageConflictKey(name, rank);
    return [
      {
        key: keyFor(VALUE_NAMES.PAYER_NAME),
        label: `Payer Name${suffix}`,
        lines: [coverage.payerName ?? ''],
        sources: sourcesFor(coverage, VALUE_NAMES.PAYER_NAME),
      },
      {
        key: keyFor(VALUE_NAMES.MEMBER_ID),
        label: `Member ID${suffix}`,
        lines: [coverage.memberId ?? ''],
        sources: sourcesFor(coverage, VALUE_NAMES.MEMBER_ID),
      },
    ].filter((cell) => cell.lines[0] !== '');
  });
}

// A coverage the packet lists at a rank the referral holds nothing at. It has no cell of its own —
// there is no coverage to show — so it gets one, reading as absent, for its highlight to sit on.
const NOT_ON_REFERRAL = 'Not on this referral';

function missingCoverageCells(conflicts: DemographicConflict[]): Cell[] {
  return conflicts.flatMap((conflict) => {
    if (!conflict.missingCoverage) return [];
    const { [VALUE_NAMES.PAYER_NAME]: payer, [VALUE_NAMES.MEMBER_ID]: member } = conflict.values;
    return [
      {
        key: conflict.key,
        label: `Insurance (${conflict.missingCoverage.label})`,
        lines: [NOT_ON_REFERRAL],
        conflict,
        current: NOT_ON_REFERRAL,
        packet: [payer && `Payer: ${payer}`, member && `Member ID: ${member}`].filter(Boolean).join(' · '),
      },
    ];
  });
}

// The fields a demographics card lays out when its step produces none of its own (Patient Demographics), in the
// order extraction would: the patient, insurance, source, what an Olio referral adds, then the address.
export const PATIENT_DEMOGRAPHIC_FIELDS: string[] = [
  VALUE_NAMES.PATIENT_FIRST_NAME,
  VALUE_NAMES.PATIENT_LAST_NAME,
  VALUE_NAMES.PATIENT_DOB,
  VALUE_NAMES.PATIENT_GENDER,
  VALUE_NAMES.PATIENT_SSN,
  COVERAGES_VALUE,
  VALUE_NAMES.REFERRAL_SOURCE,
  ...OLIO_REFERRAL_ROW,
  'patient_address_line_1',
  'patient_address_line_2',
  'patient_city',
  VALUE_NAMES.PATIENT_STATE,
  'patient_zip',
];

// `canResolve` offers Dismiss / Use this value on a highlighted field. The card passes it only when
// the viewer can edit a referral that is still open; without it the panel opens read-only.
//
// `note` and `noteLink` are the step's line above the fields. It's rendered here so the sources toggle
// can sit at the end of it, beside the card's other link, rather than on a row of its own.
export default function ExtractionResults({
  action,
  workflow,
  canResolve = false,
  note,
  noteLink,
  fields,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  canResolve?: boolean;
  note?: string;
  noteLink?: ReactNode;
  // The fields to lay out; the step's own `produces` when absent.
  fields?: string[];
}) {
  const read = (name: string) => workflow.valueFor(name)?.value;
  // A dismissed conflict renders as an ordinary field: someone looked and kept the value in use.
  const conflicts = demographicConflicts(workflow, action).filter((conflict) => !conflict.dismissed);
  const resolver = useResolveDemographicConflict(workflow, action, conflicts);
  const sources = extractionSources(action);
  // Off by default: on every field the badges crowd the card, and most fields are never checked.
  const [showSources, setShowSources] = useState(false);
  // The card sits on the page rather than in a drawer, so the viewer has no drawer to close with — and
  // no drawer's backdrop behind it, so it brings its own.
  const { openViewer, viewer } = useCitationViewer({
    attachmentType: WORKFLOW_ATTACHABLE_MODEL,
    parentOpen: true,
    backdrop: true,
  });

  // Badged while the value in use reads the same as the packet, so a badge always shows the value.
  const readsAsPacket = (name: string) => isPacketReading(name, read(name), action.outputFor(name));
  const sourcesFor = (name: string) => (readsAsPacket(name) ? sources.forValue(name) : []);
  // The packet insurance's rank word, when this coverage's field reads the same as the packet's.
  const coveragePacketRank = (coverage: ExtractedCoverage, field: string): string | undefined => {
    const packet = packetCoverageFor(workflow, action, coverage);
    const sequence = packet?.sequence?.toLowerCase();
    if (!packet || !sequence) return undefined;
    const key = field === VALUE_NAMES.PAYER_NAME ? 'payerName' : 'memberId';
    return isPacketReading(field, coverage[key], packet[key]) ? sequence : undefined;
  };
  const coverageSourcesFor = (coverage: ExtractedCoverage, field: string): ExtractionSource[] => {
    const rank = coveragePacketRank(coverage, field);
    return rank ? sources.forCoverage(rank, field) : [];
  };
  const conflictSources = (conflict: DemographicConflict) =>
    conflict.key === ADDRESS_KEY
      ? mergeSources(...Object.keys(conflict.values).map((name) => sources.forValue(name)))
      : mergeSources(
          ...conflict.fields.map((field) =>
            field.sequence ? sources.forCoverage(field.sequence, field.name) : sources.forValue(field.name)
          )
        );

  // The packet's reading of one field, formatted the way the card formats the value in use.
  const packetNode = (conflict: DemographicConflict): ReactNode => {
    if (conflict.key === PATIENT_NAME_KEY) return spelledOut((name) => conflict.values[name] ?? read(name));
    if (conflict.key === ADDRESS_KEY) return addressLines((name) => conflict.values[name] ?? read(name)).join(', ');
    const [name] = Object.keys(conflict.values);
    const value = conflict.values[name];
    if (name === VALUE_NAMES.PATIENT_SSN) return <RevealableSsn ssn={value} />;
    return scalarLines(name, value)[0] ?? value;
  };

  // Marks a cell the packet disagrees with. The SSN's highlight holds its masked text rather than
  // its reveal control — a button can't sit inside the highlight's own — and the panel shows both
  // numbers through the reveal control instead.
  const marked = (cell: Cell): Cell => {
    const conflict = conflicts.find((candidate) => candidate.key === cell.key);
    if (!conflict) return cell;
    return {
      ...cell,
      conflict,
      node: undefined,
      sources: undefined,
      packetSources: conflictSources(conflict),
      // A cell that already words its own readings (the name, a coverage the referral doesn't have) keeps them.
      current: cell.current ?? cell.node ?? cell.lines.join(' '),
      packet: cell.packet ?? packetNode(conflict),
    };
  };

  const cellFor = (name: string): Cell[] => {
    // The referral's LIVE coverages, not the extraction snapshot the value itself holds — this card
    // is where "Edit demographics" lives, and an insurance corrected or added there has to show
    // here, as the rest of the card already does for an edited DOB or name (OLIO-3332). The same
    // facets and coverage-scoped values the header chips read, so a payer shows on both or neither.
    if (name === COVERAGES_VALUE) {
      return [
        ...coverageCells(currentCoverages(workflow), conflicts, coverageSourcesFor),
        ...missingCoverageCells(conflicts),
      ].map(marked);
    }
    // A bridged referral's source is its sending building, not the packet's wording, and has nothing to resolve.
    if (name === VALUE_NAMES.REFERRAL_SOURCE && workflow.referralSourceGroup) {
      return [{ key: name, label: 'Referral Source', lines: [workflow.referralSourceGroup.name] }];
    }
    const lines =
      name === VALUE_NAMES.PLAN_TYPE_CLASSIFICATION_ID
        ? [workflow.planTypeClassification?.name ?? ''].filter(Boolean)
        : scalarLines(name, read(name));
    if (!lines.length) return [];

    const label = CARD_LABELS[name] ?? labelForValue(workflow, name);
    // The SSN renders through the shared reveal control rather than as text — same component the
    // header uses, so masked-by-default holds identically on both.
    const node = name === VALUE_NAMES.PATIENT_SSN ? <RevealableSsn ssn={String(read(name))} /> : undefined;
    return [marked({ key: name, label, lines, node, sources: sourcesFor(name) })];
  };

  const produced = fields ?? action.produces;
  const inSpec = [
    ...NAME_VALUES,
    ...PATIENT_ROW,
    ...ADDRESS_VALUES,
    ...INSURANCE_ROW,
    ...SOURCE_ROW,
    ...OLIO_REFERRAL_ROW,
  ];
  // A name with no middle name on either side still reads as the packet's: a part blank in both is
  // not compared, since a blank never counts as a packet reading.
  const filled = (value: unknown) => value != null && String(value).trim() !== '';
  const nameParts = NAME_VALUES.filter((name) => filled(read(name)) || filled(action.outputFor(name)));

  const nameCell: Cell[] =
    produced.some((name) => NAME_VALUES.includes(name)) && workflow.hasPatientName
      ? [
          marked({
            key: PATIENT_NAME_KEY,
            label: 'Name',
            lines: [workflow.patientName],
            current: spelledOut(read),
            sources: nameParts.every(readsAsPacket)
              ? mergeSources(...NAME_VALUES.map((name) => sources.forValue(name)))
              : [],
          }),
        ]
      : [];

  // Not gated on `produces`: a referral whose step predates the address can still have one typed in.
  const address = addressLines(read);
  const heldParts = ADDRESS_VALUES.filter((name) => scalarLines(name, read(name)).length);
  // Composed into one cell, like the name. Five values, but a person reads one address, so it is badged
  // only while every part it holds reads as the packet.
  const addressCell: Cell[] = address.length
    ? [
        marked({
          key: ADDRESS_KEY,
          label: 'Address',
          lines: address,
          current: address.join(', '),
          sources: heldParts.every(readsAsPacket)
            ? mergeSources(...heldParts.map((name) => sources.forValue(name)))
            : [],
        }),
      ]
    : [];

  const rows = [
    [...nameCell, ...PATIENT_ROW.filter((name) => produced.includes(name)).flatMap(cellFor)],
    addressCell,
    INSURANCE_ROW.filter((name) => produced.includes(name)).flatMap(cellFor),
    SOURCE_ROW.filter((name) => produced.includes(name)).flatMap(cellFor),
    OLIO_REFERRAL_ROW.filter((name) => produced.includes(name)).flatMap(cellFor),
    // Produced but unplaced — rendered rather than dropped, so nothing goes missing unnoticed.
    produced.filter((name) => !inSpec.includes(name)).flatMap(cellFor),
  ].filter((row) => row.length > 0);

  if (rows.length === 0) {
    return note ? (
      <StepDescription>
        {note} {noteLink}
      </StepDescription>
    ) : null;
  }

  const anySources = rows.some((row) => row.some((cell) => cell.sources?.length));
  const plainValue = (cell: Cell) =>
    cell.node ? (
      <NodeValue>{cell.node}</NodeValue>
    ) : (
      cell.lines.map((line) => <FieldValue key={line}>{line}</FieldValue>)
    );

  return (
    <>
      {(note || anySources) && (
        <StepDescription>
          {note} {noteLink}
          {noteLink && anySources && ' · '}
          {anySources && (
            <InlineLink type='button' onClick={() => setShowSources((shown) => !shown)} data-testid='toggle-sources'>
              {showSources ? 'Hide sources' : 'Show sources'}
            </InlineLink>
          )}
        </StepDescription>
      )}
      <Groups data-testid='extraction-results'>
        {rows.map((row) => (
          <Row key={row[0].key}>
            {row.map((cell) => (
              <Field key={cell.key}>
                <FieldLabel>{cell.label}</FieldLabel>
                {cell.conflict && resolver.isQueued(cell.conflict) ? (
                  // Queued: shown as it will read once applied, with a way back.
                  <Queued data-testid={`queued-${cell.conflict.key}`}>
                    <FieldValue as='div'>{cell.packet}</FieldValue>
                    <QueuedNote>
                      Packet value ·{' '}
                      <InlineLink type='button' onClick={() => resolver.unqueue(cell.conflict!)}>
                        Undo
                      </InlineLink>
                    </QueuedNote>
                  </Queued>
                ) : cell.conflict ? (
                  <Valued $afterHighlight>
                    <DemographicConflictPopover
                      conflict={cell.conflict}
                      current={cell.current}
                      packet={cell.packet}
                      packetSources={
                        cell.packetSources?.length ? (
                          <Badges data-testid='extraction-sources'>
                            <SourceBadges
                              sources={cell.packetSources}
                              attachments={workflow.attachments}
                              onOpen={openViewer}
                            />
                          </Badges>
                        ) : undefined
                      }
                      label={cell.label}
                      canResolve={canResolve}
                      saving={resolver.saving}
                      onUse={resolver.queue}
                      onDismiss={resolver.dismiss}>
                      {/* Spans, not the default paragraph: this sits inside a button. */}
                      {cell.lines.map((line) => (
                        <FieldValue as='span' key={line}>
                          {line}
                        </FieldValue>
                      ))}
                    </DemographicConflictPopover>
                  </Valued>
                ) : showSources && cell.sources?.length ? (
                  // Wrapped only when something sits beside the value, so a bare card keeps its layout.
                  <Valued>
                    {plainValue(cell)}
                    {showSources && !!cell.sources?.length && (
                      <Badges data-testid='extraction-sources'>
                        <SourceBadges sources={cell.sources} attachments={workflow.attachments} onOpen={openViewer} />
                      </Badges>
                    )}
                  </Valued>
                ) : (
                  plainValue(cell)
                )}
              </Field>
            ))}
          </Row>
        ))}
      </Groups>
      {viewer}
      {/* Every queued packet value saves together, so each step they feed re-runs once. */}
      {resolver.queuedCount > 0 && (
        <ApplyBar data-testid='apply-packet-values'>
          <BodySmall>
            {resolver.queuedCount} packet {resolver.queuedCount === 1 ? 'value' : 'values'} ready to use
          </BodySmall>
          <ApplyButtons>
            <Button size='xsmall' variant='outline-gray' disabled={resolver.saving} onClick={resolver.discard}>
              Discard
            </Button>
            <Button size='xsmall' loading={resolver.applying} disabled={resolver.saving} onClick={resolver.apply}>
              Apply
            </Button>
          </ApplyButtons>
        </ApplyBar>
      )}
      {/* The same consent Edit Demographics asks for, once for the whole batch: the union of the
          finished steps the queued values re-run. */}
      {resolver.pendingRestart && (
        <StandardModal title="Use the packet's values?" onCancel={resolver.cancelRestart}>
          <RestartConsentStep
            actions={resolver.pendingRestart}
            saving={resolver.applying}
            onConfirm={resolver.confirmRestart}
            onBack={resolver.cancelRestart}
            backLabel='Cancel'
          />
        </StandardModal>
      )}
    </>
  );
}

// A conflict's highlight overhangs its box by 6px (DemographicConflictPopover's negative margin),
// so what follows it needs that much more room to keep the same visible gap.
const Valued = styled.div<{ $afterHighlight?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $afterHighlight }) => ($afterHighlight ? 12 : 6)}px;
`;

// Centred as a row, as Valued centres the badges beside it: inline, the SSN's eye sits on the text's
// middle rather than the line's, visibly lower than a badge.
const NodeValue = styled(FieldValue)`
  display: flex;
  align-items: center;
`;

const Badges = styled.span`
  display: inline-flex;
  gap: 4px;
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
`;

const Field = styled.div`
  min-width: 120px;
`;

const Queued = styled.div`
  border-left: 2px solid ${colors.blue100};
  padding-left: 6px;
`;

const QueuedNote = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const ApplyBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
  padding: 8px 12px;
  border-radius: 4px;
  background: ${colors.blue5};

  /* The count reads as a sentence and the pair beside it is the only way to finish what was
     queued; at 375px the three do not share a line, so the buttons take the one below. */
  @media ${({ theme }) => theme.devices.mobile} {
    flex-wrap: wrap;
  }
`;

const ApplyButtons = styled.div`
  display: flex;
  gap: 8px;

  @media ${({ theme }) => theme.devices.mobile} {
    flex-basis: 100%;

    button {
      flex: 1;
      ${mobileTouchTarget}
    }
  }
`;
